import { useQuery, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { useFirebase } from '../components/Firebase';
import { userStore } from '../store/user-store';
import { CUSTOMER_PORTAL, normalizeProductCode } from '../constants/products';

const withNormalizedProductCode = item =>
  item ? { ...item, productCode: normalizeProductCode(item.productCode) } : item;
const mapNormalizedProductCode = items => items?.map(withNormalizedProductCode);

export const cacheKeys = {
  me: 'me',
  lookups: 'lookups',
  quote: 'quote',
  policy: 'policy',
  quotes: 'quotes',
  policies: 'policies',
  claimPolicy: 'claimPolicy',
  claimQuote: 'claimQuote',
  claimRenewalPolicy: 'claimRenewalPolicy',
  claimMTAPolicy: 'claimMTAPolicy'
};
const retryTimes = 2; // number of retries, excluding the first call

const retryDelay = attemptIndex => {
  if (attemptIndex === 0) return 3000; // 3 seconds before 1st retry
  if (attemptIndex === 1) return 9000; // 9 seconds before 2nd retry
  return 0;
};

const shouldRetry = (failureCount, error) => {
  // You can customize error checking here
  return failureCount <= retryTimes;
};

export const useLookups = () => {
  const firebase = useFirebase();
  return useQuery([cacheKeys.lookups], () => firebase.doGetLookups(CUSTOMER_PORTAL), {
    cacheTime: Infinity,
    staleTime: Infinity,
    retry: shouldRetry,
    retryDelay,
    onSuccess: async () => {
      userStore.isInsyncCustomer = await firebase.doIsInsyncCustomer();
    },
    onError: err => {
      console.error(err);
      Sentry.captureException(err);
    }
  });
};

export const useUserPolicies = () => {
  const firebase = useFirebase();
  const history = useHistory();
  return useQuery([cacheKeys.policies], firebase.doGetUserPolicies, {
    cacheTime: Infinity,
    retry: shouldRetry,
    retryDelay,
    select: mapNormalizedProductCode,
    onSuccess: result => {
      const ppsPending = result.filter(x => x.paymentPageStatement === 'Not Captured');
      if (ppsPending) userStore.ppsPending = ppsPending;
    },
    onError: err => {
      console.error(err);
      Sentry.captureException(err);
      history.push({ pathname: '/error', state: { message: err.message } });
    }
  });
};

export const updateMarketingOptions = async (firebase, formik) => {
  const { auth } = firebase;
  const { values } = formik;
  firebase.doUpdateMarketingOptions({
    email: auth.currentUser.email,
    marketingOptions: JSON.stringify(values.marketing)
  });
};

export const useUserQuotes = () => {
  const firebase = useFirebase();
  const history = useHistory();
  return useQuery([cacheKeys.quotes], firebase.doGetUserQuotes, {
    cacheTime: Infinity,
    retry: shouldRetry,
    retryDelay,
    select: mapNormalizedProductCode,
    onError: err => {
      console.error(err);
      Sentry.captureException(err);
      history.push({ pathname: '/error', state: { message: err.message } });
    }
  });
};

export const useUserQuote = quoteRef => {
  const firebase = useFirebase();
  const history = useHistory();
  return useQuery([cacheKeys.quote, quoteRef], () => firebase.doGetUserQuote({ quoteId: quoteRef }), {
    retry: shouldRetry,
    retryDelay,
    select: withNormalizedProductCode,
    onError: err => {
      console.error(err);
      Sentry.captureException(err);
      history.push({ pathname: '/error', state: { message: err.message } });
    }
  });
};

export const useUserPolicy = policyId => {
  const firebase = useFirebase();
  const history = useHistory();
  return useQuery([cacheKeys.policy, policyId], () => firebase.doGetUserPolicy({ policyReference: policyId }), {
    retry: shouldRetry,
    retryDelay,
    select: withNormalizedProductCode,
    onError: err => {
      console.error(err);
      Sentry.captureException(err);
      history.push({ pathname: '/error', state: { message: err.message } });
    }
  });
};

export const useClaimPolicy = policyId => {
  const firebase = useFirebase();
  const history = useHistory();
  return useQuery([cacheKeys.claimPolicy, policyId], () => firebase.doCheckPolicyClaimStatus({ id: policyId }), {
    retry: shouldRetry,
    retryDelay,
    onSuccess: async value => {
      if (value) {
        await firebase.doClaimPolicy({ id: policyId });
      }
      history.push('/');
    },
    onError: err => {
      console.error(err);
      history.push('/');
      Sentry.captureException(err);
    }
  });
};
export const useGetUserSettings = firebase => {
  const { auth } = firebase;

  return useQuery(
    [cacheKeys.userSettings, auth.currentUser.email],
    () => firebase.doGetUserSettings({ faces: '', email: auth.currentUser.email }),
    {
      retry: shouldRetry,
      retryDelay,
      onSuccess: async value => value,
      onError: err => {
        console.error(err);
        // history.push('/');
        Sentry.captureException(err);
      }
    }
  );
};

export const useClaimRenewalPolicy = encryptedId => {
  const firebase = useFirebase();
  const history = useHistory();
  const queryClient = useQueryClient();
  return useQuery(
    [cacheKeys.claimRenewalPolicy, encryptedId],
    () => firebase.doClaimRenewalPolicy({ encryptedPolicyReference: encryptedId }),
    {
      retry: shouldRetry,
      retryDelay,
      onSuccess: data => {
        queryClient.invalidateQueries(cacheKeys.policies);
        history.push(`/policies/${data.policyReference}`);
      },
      onError: err => {
        console.error(err);
        Sentry.captureException(err);
        history.push('/error');
      }
    }
  );
};

export const useClaimMTAPolicy = encryptedId => {
  const firebase = useFirebase();
  const history = useHistory();
  const queryClient = useQueryClient();
  return useQuery(
    [cacheKeys.claimMTAPolicy, encryptedId],
    () => firebase.doClaimMTAPolicy({ encryptedPolicyReference: encryptedId }),
    {
      retry: shouldRetry,
      retryDelay,
      onSuccess: data => {
        queryClient.invalidateQueries(cacheKeys.policies);
        history.push(`/policies/${data.policyReference}`);
      },
      onError: err => {
        console.error(err);
        Sentry.captureException(err);
        history.push('/error');
      }
    }
  );
};

export const useClaimQuote = quoteRef => {
  const firebase = useFirebase();
  const history = useHistory();
  return useQuery([cacheKeys.claimQuote, quoteRef], () => firebase.doCheckQuoteClaimStatus({ id: quoteRef }), {
    retry: shouldRetry,
    retryDelay,
    onSuccess: async value => {
      if (value) {
        await firebase.doClaimQuote({ id: quoteRef });
      }
      history.push('/quotes');
    },
    onError: err => {
      console.error(err);
      history.push('/');
      Sentry.captureException(err);
    }
  });
};
